import type { JsonValue } from "@e308/core";
import { cloneLifecycle, contractedEdges, type LockedMatchSetup } from "./lifecycle-state.js";
import type { MatchLifecycleState, MatchOutcome, MatchStatus } from "./lifecycle-types.js";
import { exactRecordKeys as exactKeys, recordObject } from "./record-validation.js";
import type { MatchMode } from "./types.js";

export function validateLifecycle(value: unknown): MatchLifecycleState & JsonValue {
  const source = recordObject(value, "match lifecycle");
  exactKeys(source, [
    "status",
    "mode",
    "mapId",
    "durationPreset",
    "timeLimitMs",
    "openingPeaceUntilMs",
    "overtimeStartedAtMs",
    "endedAtMs",
    "outcome",
    "eliminated",
    "borders",
  ]);
  const state = source as unknown as MatchLifecycleState;
  if (!isStatus(state.status) || !isMode(state.mode)) throw new TypeError("Invalid match status");
  if (!["duel", "ring", "crossroads"].includes(state.mapId)) throw new TypeError("Invalid map ID");
  if (!["lunch-shift", "standard-shift", "double-shift"].includes(state.durationPreset))
    throw new TypeError("Invalid duration preset");
  for (const time of [state.timeLimitMs, state.openingPeaceUntilMs]) safeTime(time);
  if (state.overtimeStartedAtMs !== null) safeTime(state.overtimeStartedAtMs);
  if (state.endedAtMs !== null) safeTime(state.endedAtMs);
  if (!Array.isArray(state.eliminated) || !Array.isArray(state.borders))
    throw new TypeError("Invalid lifecycle collections");
  const eliminated = state.eliminated.map((entry) => {
    const item = recordObject(entry, "elimination");
    exactKeys(item, ["seatId", "atGameMs"]);
    if (typeof item.seatId !== "string") throw new TypeError("Invalid elimination");
    safeTime(item.atGameMs);
    return { seatId: item.seatId, atGameMs: item.atGameMs as number };
  });
  if (new Set(eliminated.map((entry) => entry.seatId)).size !== eliminated.length)
    throw new TypeError("Duplicate elimination");
  const borders = state.borders.map(validateBorder);
  if (new Set(borders.map((border) => edgeKey(border.left, border.right))).size !== borders.length)
    throw new TypeError("Duplicate active border");
  validateOutcome(state.outcome);
  return cloneLifecycle({ ...state, eliminated, borders }) as MatchLifecycleState & JsonValue;
}

export function validateLifecycleForSetup(
  value: unknown,
  setup: LockedMatchSetup,
  seatIds: readonly string[],
): MatchLifecycleState & JsonValue {
  const state = validateLifecycle(value);
  if (
    state.mode !== setup.mode ||
    state.mapId !== setup.mapId ||
    state.durationPreset !== setup.durationPreset ||
    state.timeLimitMs !== setup.timeLimitMs ||
    state.openingPeaceUntilMs !== setup.openingPeaceMs
  )
    throw new TypeError("Lifecycle does not match locked setup");
  const ids = new Set(seatIds);
  if (
    state.eliminated.some((entry) => !ids.has(entry.seatId)) ||
    state.borders.some((border) => !ids.has(border.left) || !ids.has(border.right))
  )
    throw new TypeError("Lifecycle references an unknown seat");
  validateTopology(state, setup);
  validateStatus(state);
  return state;
}

function validateBorder(value: unknown) {
  const item = recordObject(value, "active border");
  exactKeys(item, ["left", "right", "fightingStartsAtMs"]);
  if (typeof item.left !== "string" || typeof item.right !== "string")
    throw new TypeError("Invalid active border");
  safeTime(item.fightingStartsAtMs);
  if (item.left >= item.right) throw new TypeError("Borders must use canonical seat order");
  return {
    left: item.left,
    right: item.right,
    fightingStartsAtMs: item.fightingStartsAtMs as number,
  };
}

function validateTopology(state: MatchLifecycleState, setup: LockedMatchSetup): void {
  const expected = contractedEdges(
    setup.originalEdges,
    new Set(state.eliminated.map((entry) => entry.seatId)),
  );
  if (
    state.borders.length !== expected.length ||
    state.borders.some(
      (border, index) =>
        edgeKey(border.left, border.right) !== edgeKey(...(expected[index] as [string, string])),
    )
  )
    throw new TypeError("Lifecycle borders do not match eliminated towns");
}

function validateStatus(state: MatchLifecycleState): void {
  if (state.status === "ended") {
    if (state.endedAtMs === null || state.outcome === null)
      throw new TypeError("Ended match needs outcome");
  } else if (state.endedAtMs !== null || state.outcome !== null) {
    throw new TypeError("Live match cannot have an outcome");
  }
  if (
    (state.status === "active" && state.overtimeStartedAtMs !== null) ||
    (state.status === "overtime" && state.overtimeStartedAtMs === null)
  )
    throw new TypeError("Invalid overtime state");
}

function validateOutcome(value: MatchOutcome | null): void {
  if (value === null) return;
  const source = recordObject(value, "match outcome");
  exactKeys(source, ["kind", "winnerId"]);
  if (source.kind === "draw" && source.winnerId === null) return;
  if (
    (source.kind === "seat-win" || source.kind === "team-win") &&
    typeof source.winnerId === "string"
  )
    return;
  throw new TypeError("Invalid match outcome");
}

function safeTime(value: unknown): void {
  if (!Number.isSafeInteger(value) || (value as number) < 0)
    throw new TypeError("Invalid match time");
}

function edgeKey(left: string, right: string): string {
  return left < right ? `${left}:${right}` : `${right}:${left}`;
}

function isStatus(value: unknown): value is MatchStatus {
  return value === "active" || value === "overtime" || value === "ended";
}

function isMode(value: unknown): value is MatchMode {
  return (
    value === "every-town-for-itself" || value === "team-shift" || value === "private-practice"
  );
}
